import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import './MiaPlatformSelector.css';
import { devLog } from '../utils/devLog';

/**
 * Reusable dropdown selector component for Mia Platform entities (tenants/projects)
 * Matches the style from the Mia Platform Console UI
 * Supports server-side search and infinite scroll
 */
const MiaPlatformSelector = ({
  label,
  items,
  selectedItem,
  onSelect,
  placeholder,
  icon,
  loading,
  error,
  searchable = true,
  emptyMessage = 'No items found',
  onCreateNew,
  showCreateButton = false,
  onSearch,
  hasMore = false,
  onLoadMore,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setDebouncedSearch('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Trigger server-side search when debounced search changes
  useEffect(() => {
    if (onSearch && debouncedSearch !== undefined) {
      onSearch(debouncedSearch);
    }
  }, [debouncedSearch, onSearch]);

  // Client-side filter items based on search query (fallback if no server-side search)
  const filteredItems = onSearch ? items : items.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      (item.id && item.id.toLowerCase().includes(query))
    );
  });

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasMore || loading || !onLoadMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Load more when scrolled to 80% of the list
    if (scrollPercentage > 0.8) {
      onLoadMore();
    }
  }, [hasMore, loading, onLoadMore]);

  // Attach scroll listener
  useEffect(() => {
    const listElement = listRef.current;
    if (listElement && hasMore && onLoadMore) {
      listElement.addEventListener('scroll', handleScroll);
      return () => {
        listElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, hasMore, onLoadMore]);

  const handleSelect = (item) => {
    // eslint-disable-next-line no-console
    devLog('✅ MiaPlatformSelector - Item selected:', item);
    onSelect(item);
    setIsOpen(false);
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const displayText = selectedItem?.name || placeholder || 'Select...';

  // Show search input if searchable and either has onSearch or more than 5 items
  const showSearch = searchable && (onSearch || items.length > 5);

  return (
    <div className="mia-selector-container" ref={dropdownRef}>
      <button
        className="mia-selector-button"
        disabled={loading || error}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <div className="mia-selector-label">
          {icon && <span className="mia-selector-icon">{icon}</span>}
          <span className="mia-selector-text">{displayText}</span>
        </div>
        <span className={`mia-selector-arrow ${isOpen ? 'open' : ''}`}>{'▼'}</span>
      </button>

      {isOpen && (
        <div className="mia-selector-dropdown">
          {label && (
            <div className="mia-selector-header">{label}</div>
          )}

          {showSearch && (
            <div className="mia-selector-search">
              <input
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Find a ${label || 'item'}...`}
                type="text"
                value={searchQuery}
              />
            </div>
          )}

          <div className="mia-selector-list" ref={listRef}>
            {loading && filteredItems.length === 0 ? (
              <div className="mia-selector-loading">
                <span className="mia-selector-loading-spinner" />
                {'Loading...'}
              </div>
            ) : error ? (
              <div className="mia-selector-error">
                {error}
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="mia-selector-empty">
                {emptyMessage}
              </div>
            ) : (
              <>
                {filteredItems.map((item) => (
                  <button
                    className={`mia-selector-item ${selectedItem?.id === item.id ? 'selected' : ''}`}
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    type="button"
                  >
                    <div className="mia-selector-item-name">{item.name}</div>
                    {item.id && (
                      <div className="mia-selector-item-id">{item.id}</div>
                    )}
                  </button>
                ))}
                {loading && hasMore && (
                  <div className="mia-selector-loading-more">
                    <span className="mia-selector-loading-spinner" />
                    {'Loading more...'}
                  </div>
                )}
              </>
            )}
          </div>

          {!loading && !error && items.length > 0 && showCreateButton && onCreateNew && (
            <div className="mia-selector-view-all">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onCreateNew();
                }}
                type="button"
              >
                <span>{'+'}</span>
                {`Create New ${label || 'Item'}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

MiaPlatformSelector.propTypes = {
  emptyMessage: PropTypes.string,
  error: PropTypes.string,
  hasMore: PropTypes.bool,
  icon: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  label: PropTypes.string,
  loading: PropTypes.bool,
  onCreateNew: PropTypes.func,
  onLoadMore: PropTypes.func,
  onSearch: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  selectedItem: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  showCreateButton: PropTypes.bool,
};

export default MiaPlatformSelector;
